import { usePeriods } from '@/services/usePeriods';
import { useState, useEffect, useRef } from "react";
import { Plus, Trash2, Edit2, Building2, Loader2, Search, ChevronLeft, ChevronRight, Users, Upload, FileUp, AlertTriangle, Shield } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAdminConferenceRooms, useCreateConferenceRoom, useUpdateConferenceRoom, useDeleteConferenceRoom, useImportConferenceRooms, type ConferenceRoom, useSetModerator } from "@/services/adminConferenceRoomService";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import toast from "react-hot-toast";

export default function RoomsPage() {
    const { data: periods } = usePeriods();
    const activePeriod = periods?.find((p: any) => p.is_active);
    const [filterPeriod, setFilterPeriod] = useState<string>("all");
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    useEffect(() => {
        if (activePeriod && filterPeriod === "all") {
            setFilterPeriod(String(activePeriod.period_id));
        }
    }, [activePeriod, filterPeriod]);
    const [filterSearch, setFilterSearch] = useState<string>("");
    const [filterDate, setFilterDate] = useState<string>("");
    const [filterConference, setFilterConference] = useState<string>("all");

    // Fetch Conferences
    const { data: conferences, isLoading: isConferencesLoading } = useQuery({
        queryKey: ["conferencesList"],
        queryFn: async () => {
            const { data } = await axiosClient.get("/private/conference-billing/accessible-conferences");
            return data.data;
        }
    });

    // Determine query param for rooms fetch
    const conferenceIdParam = filterConference !== "all" ? filterConference : undefined;

    // Fetch Rooms
    const { data: rooms, isLoading } = useAdminConferenceRooms(conferenceIdParam);

    // Filter Data Local (Search & Date)
    const filteredRooms = rooms?.filter((room: ConferenceRoom) => {
        let match = true;
        if (filterSearch) {
            const s = filterSearch.toLowerCase();
            match = match && (
                room.name?.toLowerCase().includes(s) ||
                room.description?.toLowerCase().includes(s) || false
            );
        }
        if (filterDate) {
            match = match && (room.date === filterDate);
        }
        return match;
    }) || [];


    // Pagination Logic
    const totalItems = filteredRooms.length;
    const totalPages = Math.ceil(totalItems / perPage);
    const startIndex = (page - 1) * perPage;
    const endIndex = Math.min(startIndex + perPage, totalItems);
    const currentRooms = filteredRooms.slice(startIndex, endIndex);
    // Mutations
    const createRoom = useCreateConferenceRoom();
    const updateRoom = useUpdateConferenceRoom();
    const deleteRoom = useDeleteConferenceRoom();
    const setModerator = useSetModerator();
    
    const [isModeratorModalOpen, setIsModeratorModalOpen] = useState(false);
    const [selectedRoomForModerator, setSelectedRoomForModerator] = useState<ConferenceRoom | null>(null);
    const [moderatorName, setModeratorName] = useState("");
    const [moderatorEmail, setModeratorEmail] = useState("");
    const importRooms = useImportConferenceRooms();

    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importDate, setImportDate] = useState<string>("");
    const [importCapacity, setImportCapacity] = useState<number | "">("");
    const [importWarnings, setImportWarnings] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleSetModerator = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedRoomForModerator) return;
        try {
            await setModerator.mutateAsync({
                id: selectedRoomForModerator.id,
                payload: { name: moderatorName, email: moderatorEmail }
            });
            toast.success("Moderator berhasil diatur!");
            setIsModeratorModalOpen(false);
            setModeratorName("");
            setModeratorEmail("");
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Gagal mengatur moderator.");
        }
    };

    const handleImportSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!importFile) { toast.error('Pilih file excel terlebih dahulu'); return; }
        const formData = new FormData();
        formData.append('file', importFile);
        if (importDate) formData.append('date', importDate);
        if (importCapacity !== "") formData.append('capacity', importCapacity.toString());
        importRooms.mutate(formData, {
            onSuccess: (data: any) => {
                toast.success(data.message || 'Import berhasil');
                if (data.warnings && data.warnings.length > 0) {
                    setImportWarnings(data.warnings);
                } else {
                    setIsImportModalOpen(false);
                    setImportFile(null);
                    setImportDate("");
                    setImportCapacity("");
                }
            },
            onError: (error: any) => {
                const message = error.response?.data?.message || 'Gagal import data';
                toast.error(typeof message === 'string' ? message : JSON.stringify(message));
            }
        });
    };

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formData, setFormData] = useState({
        period_id: "",
        conference_id: "",
        name: "",
        capacity: "",
        description: "",
        date: ""
    });

    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

    const handleOpenModal = (room?: ConferenceRoom) => {
        if (room) {
            setEditingId(room.id);
            setFormData({
                period_id: room.period_id ? String(room.period_id) : (filterPeriod !== "all" ? filterPeriod : (activePeriod ? String(activePeriod.period_id) : "")),
                conference_id: room.conference_id ? String(room.conference_id) : "",
                name: room.name || "",
                capacity: room.capacity ? String(room.capacity) : "",
                description: room.description || "",
                date: room.date || ""
            });
        } else {
            setEditingId(null);
            setFormData({
                period_id: filterPeriod !== "all" ? filterPeriod : (activePeriod ? String(activePeriod.period_id) : ""),
                conference_id: filterConference !== "all" ? filterConference : "",
                name: "",
                capacity: "",
                description: "",
                date: ""
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name || !formData.period_id) {
            toast.error("Nama ruangan dan Periode wajib diisi.");
            return;
        }

        const payload: any = {
            period_id: parseInt(formData.period_id),
            name: formData.name,
            capacity: formData.capacity ? parseInt(formData.capacity) : null,
            description: formData.description,
            date: formData.date || null
        };
        if (formData.conference_id && formData.conference_id !== 'global') {
            payload.conference_id = parseInt(formData.conference_id);
        }

        try {
            if (editingId) {
                await updateRoom.mutateAsync({ id: editingId, payload });
                toast.success("Ruangan berhasil diperbarui!");
            } else {
                await createRoom.mutateAsync(payload);
                toast.success("Ruangan berhasil ditambahkan!");
            }
            setIsModalOpen(false);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Terjadi kesalahan saat menyimpan data.");
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            {/* Header Card */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col xl:flex-row xl:items-center justify-between gap-6 relative overflow-hidden">
                <div className="absolute -top-12 -right-12 p-12 opacity-5 pointer-events-none">
                    <Building2 className="w-64 h-64 text-blue-600" />
                </div>

                <div className="flex items-start gap-5 relative z-10">
                    <div className="p-4 rounded-3xl bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400 flex-shrink-0 border border-blue-100 dark:border-blue-800/50 shadow-sm">
                        <Building2 className="w-8 h-8" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">
                            Ruangan Konferensi
                        </h1>
                        <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl">
                            Kelola data ruangan untuk keperluan presentasi dan kegiatan konferensi (Track).
                        </p>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3 relative z-10">
                    <Button onClick={() => { setIsImportModalOpen(true); setImportWarnings([]); setImportFile(null); setImportDate(""); setImportCapacity(""); }} variant="outline" className="h-12 px-6 rounded-xl border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800 font-semibold text-slate-700 dark:text-slate-300 shadow-sm">
                        <Upload className="w-5 h-5 mr-2" />
                        Import Data
                    </Button>
                    <Button onClick={() => handleOpenModal()} className="h-12 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-600/20">
                        <Plus className="w-5 h-5 mr-2" />
                        Tambah Ruangan
                    </Button>
                </div>
            </div>

            {/* List */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
                {/* Search & Filter */}
                <div className="p-5 md:p-6 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-200/70 dark:border-slate-800 flex flex-col sm:flex-row items-center gap-4">
                    <div className="w-full sm:w-[320px]">
                        <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                            <SelectTrigger className="w-full h-11 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-xl select-none">
                                <SelectValue placeholder="Pilih Periode" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl w-[var(--radix-select-trigger-width)] max-h-[300px]">
                                <SelectItem value="all">Semua Periode</SelectItem>
                                {periods?.map((period) => (
                                    <SelectItem key={period.period_id} value={String(period.period_id)}>
                                        {period.name} {period.is_active && "(Aktif)"}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="w-full sm:w-[350px]">
                        <Select value={filterConference} onValueChange={setFilterConference}>
                            <SelectTrigger className="w-full h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-left select-none">
                                <div className="truncate w-full text-left min-w-0">
                                    <SelectValue placeholder="Pilih Track atau biarkan kosong (Global)" />
                                </div>
                            </SelectTrigger>
                            <SelectContent className="rounded-xl w-[var(--radix-select-trigger-width)] max-h-[300px]">
                                <SelectItem value="all">Semua Track Konferensi</SelectItem>
                                <SelectItem value="global" className="whitespace-normal break-words text-slate-500 italic">
                                        — Tanpa Track (Room Global) —
                                    </SelectItem>
                                    {conferences?.map((conf: any) => (
                                    <SelectItem key={conf.conference_id} value={String(conf.conference_id)} className="whitespace-normal break-words">
                                        {conf.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="relative flex-1 w-full max-w-sm ml-auto flex gap-3">
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                <Search className="h-4 w-4 text-slate-400" />
                            </div>
                            <Input
                                type="text"
                                placeholder="Cari ruangan..."
                                value={filterSearch}
                                onChange={(e) => setFilterSearch(e.target.value)}
                                className="pl-10 h-11 w-full bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl"
                            />
                        </div>
                        <div className="w-[180px]">
                            <Input
                                type="date"
                                value={filterDate}
                                onChange={(e) => setFilterDate(e.target.value)}
                                className="h-11 w-full bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl"
                            />
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-sm">
                                <th className="px-6 py-4 font-semibold w-16 text-center">No</th>
                                <th className="px-6 py-4 font-semibold">Nama Ruangan</th>
                                <th className="px-6 py-4 font-semibold">Track Konferensi</th>
                                <th className="px-6 py-4 font-semibold text-center">Tanggal</th>
                                <th className="px-6 py-4 font-semibold text-center">Kapasitas & Ploting</th>
                                <th className="px-6 py-4 font-semibold text-right w-32 sticky right-0 bg-slate-50/80 dark:bg-slate-800/50 backdrop-blur-sm z-10 border-l border-slate-200 dark:border-slate-800 shadow-[-4px_0_10px_rgba(0,0,0,0.02)] dark:shadow-[-4px_0_10px_rgba(0,0,0,0.2)]">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                            {isLoading || isConferencesLoading ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
                                        <p>Memuat data ruangan...</p>
                                    </td>
                                </tr>
                            ) : filteredRooms.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                                        Data tidak ditemukan.
                                    </td>
                                </tr>
                            ) : (
                                currentRooms.map((room, index) => {
                                    const conference = conferences?.find((c: any) => c.conference_id === room.conference_id);
                                    return (
                                        <tr key={room.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                                            <td className="px-6 py-4 text-center font-medium text-slate-500 dark:text-slate-400">
                                                {startIndex + index + 1}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="font-bold text-slate-900 dark:text-white mb-1">{room.name}</div>
                                                {room.description && (
                                                    <div className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">
                                                        {room.description}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                    {conference?.name || 'Unknown Track'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-sm text-slate-600 dark:text-slate-300">
                                                    {room.date || "-"}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="flex flex-col items-center gap-1.5">
                                                    <span className="inline-flex px-2.5 py-0.5 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                                        {room.capacity ? 'Maks: ' + room.capacity + ' kursi' : 'Tidak terbatas'}
                                                    </span>
                                                    <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/30 px-2 py-0.5 rounded-full border border-blue-100 dark:border-blue-800/50">
                                                        Diplot: {room.attendances_count || 0}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right sticky right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm z-10 border-l border-slate-100 dark:border-slate-800/60 shadow-[-4px_0_10px_rgba(0,0,0,0.02)] dark:shadow-[-4px_0_10px_rgba(0,0,0,0.2)]">
                                                <div className="flex items-center justify-end gap-1">
                                                    <TooltipProvider delayDuration={200}>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button asChild variant="ghost" size="icon" className="h-8 w-8 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50">
                                                                    <Link to={`/admin/conference-rooms/${room.id}/participants?conference_id=${room.conference_id}`}>
                                                                        <Users className="w-4 h-4" />
                                                                    </Link>
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p>Kelola Peserta</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>

                                                                                                        <TooltipProvider delayDuration={200}>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-8 w-8 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
                                                                    onClick={() => {
                                                                        setSelectedRoomForModerator(room);
                                                                        setModeratorName(room.moderator?.name || "");
                                                                        setModeratorEmail(room.moderator?.email || "");
                                                                        setIsModeratorModalOpen(true);
                                                                    }}
                                                                >
                                                                    <Shield className="w-4 h-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p>Kelola Moderator</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>

<TooltipProvider delayDuration={200}>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button 
                                                                    variant="ghost" 
                                                                    size="icon"
                                                                    onClick={() => handleOpenModal(room)}
                                                                    className="h-8 w-8 bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/50"
                                                                >
                                                                    <Edit2 className="w-4 h-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p>Edit Ruangan</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>

                                                    <TooltipProvider delayDuration={200}>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button 
                                                                    variant="ghost" 
                                                                    size="icon"
                                                                    onClick={() => setDeleteConfirmId(room.id)}
                                                                    className="h-8 w-8 bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p>Hapus Ruangan</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer Pagination */}
                {totalItems > 0 && !isLoading && (
                    <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-3">
                            <span className="text-slate-500 font-medium text-xs sm:text-sm">
                                Menampilkan <span className="font-semibold text-slate-700 dark:text-slate-300">{startIndex + 1} - {endIndex}</span> dari <span className="font-semibold text-slate-700 dark:text-slate-300">{totalItems}</span> data
                            </span>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                            <div className="flex items-center gap-2">
                                <span className="text-slate-500 text-xs sm:text-sm font-medium">Per halaman:</span>
                                <select
                                    value={perPage}
                                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                    className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500 font-bold text-slate-700 dark:text-slate-300 text-xs sm:text-sm shadow-sm"
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="h-8 px-3 rounded-lg border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 font-medium"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" />
                                    <span className="hidden sm:inline">Prev</span>
                                </Button>
                                <span className="text-slate-600 dark:text-slate-400 font-medium text-xs sm:text-sm min-w-[3rem] text-center">
                                    {page} / {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages || totalPages === 0}
                                    className="h-8 px-3 rounded-lg border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 font-medium"
                                >
                                    <span className="hidden sm:inline">Next</span>
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal Tambah/Edit */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="sm:max-w-lg p-0 overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <DialogHeader className="p-6 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                        <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                            {editingId ? "Edit Ruangan" : "Tambah Ruangan"}
                        </DialogTitle>
                        <DialogDescription className="text-slate-500 mt-1.5">
                            Atur detail ruangan dan pilih track konferensi yang sesuai.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto max-h-[75vh] custom-scrollbar">
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Periode <span className="text-red-500">*</span></Label>
                            <Select value={formData.period_id} onValueChange={(val) => setFormData({ ...formData, period_id: val })}>
                                <SelectTrigger className="w-full overflow-hidden h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-left select-none">
                                    <div className="truncate w-full text-left min-w-0">
                                        <SelectValue placeholder="Pilih Periode" />
                                    </div>
                                </SelectTrigger>
                                <SelectContent className="rounded-xl w-[var(--radix-select-trigger-width)] max-h-[250px]">
                                    {periods?.map((period: any) => (
                                        <SelectItem key={period.period_id} value={String(period.period_id)} className="whitespace-normal break-words">
                                            {period.name} {period.is_active && "(Aktif)"}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Track Konferensi <span className="text-slate-400 text-xs font-normal">(Opsional)</span></Label>
                            <Select value={formData.conference_id} onValueChange={(val) => setFormData({ ...formData, conference_id: val })}>
                                <SelectTrigger className="w-full overflow-hidden h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-left select-none">
                                    <div className="truncate w-full text-left min-w-0">
                                        <SelectValue placeholder="Pilih Track atau biarkan kosong (Global)" />
                                    </div>
                                </SelectTrigger>
                                <SelectContent className="rounded-xl w-[var(--radix-select-trigger-width)] max-h-[300px]">
                                    <SelectItem value="global" className="whitespace-normal break-words text-slate-500 italic">
                                        — Tanpa Track (Room Global) —
                                    </SelectItem>
                                    {conferences?.map((conf: any) => (
                                        <SelectItem key={conf.conference_id} value={String(conf.conference_id)} className="whitespace-normal break-words">
                                            {conf.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="name" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Nama Ruangan <span className="text-red-500">*</span></Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Misal: Track 1 - Room A"
                                className="h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
                                required
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="capacity" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Kapasitas Maksimal</Label>
                                <Input
                                    id="capacity"
                                    type="number"
                                    min="0"
                                    value={formData.capacity}
                                    onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                                    placeholder="Opsional"
                                    className="h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="date" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Tanggal Kegiatan</Label>
                                <Input
                                    id="date"
                                    type="date"
                                    value={formData.date}
                                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                    className="h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 block w-full"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="description" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Keterangan / Lokasi</Label>
                            <Textarea
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Deskripsi lokasi atau catatan tambahan..."
                                className="resize-none h-20 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
                            />
                        </div>

                        <DialogFooter className="pt-4 mt-6 border-t border-slate-200 dark:border-slate-800">
                            <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="h-11 px-6 rounded-xl">
                                Batal
                            </Button>
                            <Button type="submit" disabled={createRoom.isPending || updateRoom.isPending} className="h-11 px-6 rounded-xl bg-blue-600 hover:bg-blue-700">
                                {(createRoom.isPending || updateRoom.isPending) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Simpan
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirm Modal */}
            <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
                <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900">
                    <DialogHeader>
                        <DialogTitle>Hapus Ruangan?</DialogTitle>
                        <DialogDescription>
                            Tindakan ini tidak dapat dibatalkan. Menghapus ruangan akan memengaruhi data peserta yang sudah ditugaskan ke ruangan ini.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-6">
                        <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>Batal</Button>
                        <Button 
                            variant="destructive" 
                            disabled={deleteRoom.isPending}
                            onClick={async () => {
                                if (!deleteConfirmId) return;
                                try {
                                    await deleteRoom.mutateAsync(deleteConfirmId);
                                    toast.success("Ruangan berhasil dihapus!");
                                    setDeleteConfirmId(null);
                                } catch (error) {
                                    toast.error("Gagal menghapus ruangan.");
                                }
                            }}
                        >
                            {deleteRoom.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : "Ya, Hapus"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
                        </Dialog>

            
            <Dialog open={isModeratorModalOpen} onOpenChange={setIsModeratorModalOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Kelola Moderator Ruangan</DialogTitle>
                        <DialogDescription>
                            Atur moderator untuk <strong>{selectedRoomForModerator?.name}</strong>.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSetModerator} className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nama Moderator</Label>
                            <Input
                                placeholder="Nama lengkap moderator"
                                value={moderatorName}
                                onChange={(e) => setModeratorName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Email Moderator</Label>
                            <Input
                                type="email"
                                placeholder="Email moderator"
                                value={moderatorEmail}
                                onChange={(e) => setModeratorEmail(e.target.value)}
                                required
                            />
                        </div>
                        <DialogFooter className="mt-6">
                            <Button type="button" variant="outline" onClick={() => setIsModeratorModalOpen(false)}>Batal</Button>
                            <Button type="submit" disabled={setModerator.isPending}>
                                {setModerator.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : "Simpan"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={isImportModalOpen} onOpenChange={setIsImportModalOpen}>
                <DialogContent className="sm:max-w-[500px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-3xl p-0 overflow-hidden shadow-2xl">
                    <DialogHeader className="p-6 pb-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                        <DialogTitle className="text-xl font-bold flex items-center gap-3 text-slate-800 dark:text-white">
                            <div className="p-2.5 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
                                <Upload className="w-5 h-5" />
                            </div>
                            Import Parallel Session
                        </DialogTitle>
                    </DialogHeader>
                    
                    {importWarnings.length > 0 ? (
                        <div className="p-6 bg-yellow-50 dark:bg-yellow-900/10 max-h-[400px] overflow-y-auto">
                            <h4 className="font-semibold text-yellow-800 dark:text-yellow-500 mb-3 flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5" />
                                Peringatan Import
                            </h4>
                            <ul className="list-disc pl-5 space-y-1.5 text-sm text-yellow-700 dark:text-yellow-600/90 font-medium">
                                {importWarnings.map((w, i) => <li key={i}>{w}</li>)}
                            </ul>
                            <div className="mt-6 flex justify-end">
                                <Button onClick={() => setIsImportModalOpen(false)} className="bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl">
                                    Mengerti & Tutup
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleImportSubmit} className="p-6 flex flex-col gap-6">
                            <div className="text-sm text-slate-500 dark:text-slate-400">
                                Unggah file Excel (<strong>.xlsx</strong>) yang berisi pembagian ruangan (RoomPaper) dan daftar moderator (RoomModerator). Pastikan format tabel sesuai dengan template standar sistem.
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="mb-2 block">Tanggal Presentasi (Opsional)</Label>
                                    <Input type="date" value={importDate} onChange={(e) => setImportDate(e.target.value)} />
                                </div>
                                <div>
                                    <Label className="mb-2 block">Kapasitas Default (Opsional)</Label>
                                    <Input type="number" min="1" placeholder="Contoh: 100" value={importCapacity} onChange={(e) => setImportCapacity(e.target.value ? parseInt(e.target.value) : "")} />
                                </div>
                            </div>
                            
                            <div className="relative group">
                                <div className="absolute inset-0 bg-blue-50 dark:bg-blue-900/10 border-2 border-dashed border-blue-200 dark:border-blue-800/50 rounded-2xl group-hover:border-blue-400 dark:group-hover:border-blue-500 transition-colors pointer-events-none flex flex-col items-center justify-center p-6 gap-3">
                                    <div className="p-3 bg-white dark:bg-slate-800 rounded-full shadow-sm text-blue-500">
                                        <FileUp className="w-6 h-6" />
                                    </div>
                                    <div className="text-center">
                                        <p className="font-semibold text-slate-700 dark:text-slate-200">Pilih File Excel</p>
                                        <p className="text-xs text-slate-500 mt-1">Hanya format .xlsx atau .xls</p>
                                    </div>
                                </div>
                                <Input 
                                    type="file" 
                                    accept=".xlsx, .xls" 
                                    ref={fileInputRef} 
                                    onChange={(e) => e.target.files && setImportFile(e.target.files[0])} 
                                    className="w-full h-[140px] opacity-0 cursor-pointer"
                                />
                            </div>

                            {importFile && (
                                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl">
                                    <FileUp className="w-5 h-5 text-emerald-500" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate">{importFile.name}</p>
                                        <p className="text-xs text-slate-500">{(importFile.size / 1024).toFixed(1)} KB</p>
                                    </div>
                                    <Button type="button" variant="ghost" size="icon" onClick={() => { setImportFile(null); if(fileInputRef.current) fileInputRef.current.value = ''; }} className="text-rose-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg">
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-2 border-t border-slate-100 dark:border-slate-800 mt-2">
                                <Button type="button" variant="outline" onClick={() => setIsImportModalOpen(false)} className="rounded-xl border-slate-200 dark:border-slate-700">
                                    Batal
                                </Button>
                                <Button type="submit" disabled={importRooms.isPending || !importFile} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white shadow-md transition-all">
                                    {importRooms.isPending ? 'Memproses...' : 'Mulai Import'}
                                </Button>
                            </div>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}